/**
 * TableFooter Component
 *
 * This component renders the footer of a table, specifically handling actions such as viewing data per currency and expanding a summary of currency data.
 * It conditionally displays different footer actions depending on the type of column, including buttons for expanding data or viewing data per currency.
 * It also handles interaction logic such as showing or hiding currency lists, and it listens for clicks outside to close the currency list.
 *
 * Props:
 *  - `column` (string): The name of the column for which the footer is being rendered. This determines the behavior and actions of the footer.
 *  - `isLastColumn` (boolean): A flag indicating whether the column is the last column in the table. It helps in applying border styles.
 *  - `isFirstColumnAfterCCY` (boolean): A flag to identify if the column is the first column after the currency column. Used for styling.
 *
 * State/Refs:
 *  - `shouldExpandCurrencies` (boolean): A state that controls whether the currency list is expanded or collapsed.
 *  - `menuRef` (RefObject<HTMLDivElement>): Reference to the footer cell that contains the button, used for detecting clicks outside to close the currency list.
 *  - `rowDensityClass`: Class related to the row density, derived from the custom `useRowDensity` hook, used for styling rows.
 *
 * Effects/Actions:
 *  - `useCloseOnScrollAndClickOutside`: Hook used to close the currency list if the user clicks or scrolls outside of the footer cell.
 *
 * Logic:
 *  - **Geo Columns**: The component skips rendering for geo columns excluding "LOCATION" by using the `isGeoColumnExcludingLocation` utility.
 *  - **Location Column**: Displays a "View Per Currency" button which triggers the `handleViewPerCurrency` action.
 *  - **Currency Column**: Displays a button to "Expand Sum" which, when clicked, triggers the `handleExpandSum` action. If there is only one available currency, it fetches stats for expanding the sum.
 *  - **Footer Actions**: The component dynamically assigns a footer action (button) depending on the column type (Location or Currency).
 *
 * Example Usage:
 *  <TableFooter
 *    column="CCY"
 *    isLastColumn={false}
 *    isFirstColumnAfterCCY={true}
 *  />
 */

import React, { useRef, useState } from 'react';
import { t } from 'i18next';
import { StatsTableColumn } from '../../../../../enum/TableColumn';
import { GeoColumns } from '../../../../../enum/GeoDivisionColumnName';
import { borderStyle, getTableClass, isGeoColumnExcludingLocation } from '../../../../../util/table-helper';
import currencyIcon from '../../../../../assets/img/svg/table/currency.svg';
import upArrowIcon from '../../../../../assets/img/svg/table/up-arrow.svg';
import Button from '../../Button';
import store from '../../../../../store/store';
import { clearCurrencyData, fetchStats, setShouldViewPerCurrency } from '../../../../../store/slice/stats-slice';
import { useRowDensity } from '../../../../../hook/useRowDensity';
import { StatsFetchType } from '../../../../../enum/StatsFetchType';
import CurrencyList from './CurrencyList/CurrencyList';
import { useCloseOnScrollAndClickOutside } from '../../../../../hook/useCloseOnScrollAndClickOutside';
import { useStatsPayload } from '../../../../../hook/useStatsPayload';

interface TableFooterProps {
  column: string;
  isLastColumn: boolean;
  isFirstColumnAfterCCY: boolean;
}

const TableFooter: React.FC<TableFooterProps> = ({ column, isLastColumn, isFirstColumnAfterCCY }) => {
  const statsPayload = useStatsPayload();
  const [shouldExpandCurrencies, setShouldExpandCurrencies] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { rowDensityClass } = useRowDensity();
  useCloseOnScrollAndClickOutside(setShouldExpandCurrencies, menuRef);

  // Skip irrelevant geo columns
  if (isGeoColumnExcludingLocation(column)) {
    return (
      <div className={`table_footer ${getTableClass(column, 'footer')}`}>
        <div className={`table_row ${getTableClass(column, 'footer_row')}`}>
          <div className={`table_cell ${rowDensityClass}`} />
        </div>
      </div>
    );
  }

  const handleViewPerCurrency = async () => {
    // Clear existing currency data and fetch stats for the "View Per Currency" action
    store.dispatch(clearCurrencyData());
    await store.dispatch(
      fetchStats({
        type: StatsFetchType.VIEW_PER_CURRENCY,
        request: { ...statsPayload, groupBy: 8 },
      }),
    );
    store.dispatch(setShouldViewPerCurrency());
  };

  const handleExpandSum = async () => {
    // If there is only one available currency, fetch stats for expanding the sum
    if (store.getState().stats.availableCurrencies.length === 1) {
      await store.dispatch(
        fetchStats({
          type: StatsFetchType.EXPAND_SUM,
          request: { ...statsPayload, groupBy: 8 },
        }),
      );
    }
    // Toggle the currency list visibility
    setShouldExpandCurrencies((prev) => !prev);
  };

  // Define the footer actions for specific columns
  const footerActions: Record<string, { label: string; icon?: string; action: () => void }> = {
    [GeoColumns.LOCATION]: {
      label: t('statisticPage.table.buttons.viewPerCurrency'),
      icon: currencyIcon,
      action: handleViewPerCurrency,
    },
    [StatsTableColumn.CCY]: {
      label: t('statisticPage.table.buttons.expandSum'),
      action: handleExpandSum,
    },
  };

  // Retrieve the appropriate footer action based on the current column
  const footerAction = footerActions[column];

  return (
    <div className={`table_footer ${getTableClass(column, 'footer')}`}>
      <div className={`table_row ${getTableClass(column, 'footer_row')}`}>
        <div
          className={`table_cell ${rowDensityClass}`}
          ref={footerAction ? menuRef : undefined}
          style={
            !footerAction
              ? borderStyle({
                  isFull: true,
                  isLastColumn,
                  isFirstColumnAfterCCY,
                })
              : undefined
          }
        >
          {footerAction ? (
            <>
              {/* Render Currency List for Currency column */}
              {column === StatsTableColumn.CCY && <CurrencyList shouldExpandCurrencies={shouldExpandCurrencies} />}
              <Button
                className={`button ${rowDensityClass}`}
                icon={footerAction.icon}
                iconRight={column === StatsTableColumn.CCY ? upArrowIcon : undefined}
                onClick={footerAction.action}
              >
                {footerAction.label}
              </Button>
            </>
          ) : (
            '-'
          )}
        </div>
      </div>
    </div>
  );
};

export default TableFooter;
